import React from 'react';
import { Avatar, AvatarProps } from '@mui/material';
import DefaultAvatar from './DefaultAvatar';

interface UserAvatarProps extends Omit<AvatarProps, 'children'> {
  src?: string;
  name?: string;
  email?: string;
  size?: number;
  fallbackToDefault?: boolean;
  forceDefault?: boolean;
}

const UserAvatar: React.FC<UserAvatarProps> = ({ 
  src, 
  name, 
  email, 
  size = 40, 
  fallbackToDefault = true,
  forceDefault = false,
  sx, 
  ...props 
}) => {
  // If forceDefault is true, always show default avatar
  if (forceDefault) {
    return (
      <DefaultAvatar
        name={name}
        email={email}
        size={size}
        sx={sx}
        {...props}
      />
    );
  }

  // Debug logging
  console.log('UserAvatar - src:', src, 'name:', name, 'email:', email);

  // If we have a valid image source (including data URLs from cropping), use it
  if (src && src.trim() && 
      !src.includes('placeholder') &&
      !src.includes('unsplash.com') &&
      !src.includes('default') &&
      !src.includes('gravatar') &&
      !src.includes('avatar-placeholder') &&
      !src.includes('via.placeholder.com') &&
      !src.includes('dummyimage.com') &&
      !src.includes('images.unsplash.com')) {
    console.log('UserAvatar - Using real image:', src);
    return (
      <Avatar
        {...props}
        src={src}
        sx={{
          width: size,
          height: size,
          ...sx,
        }}
      />
    );
  }

  console.log('UserAvatar - Using default avatar for:', name || email);

  // If fallback is disabled and no valid image, return empty avatar
  if (!fallbackToDefault) {
    return (
      <Avatar
        {...props}
        sx={{
          width: size,
          height: size,
          ...sx,
        }}
      />
    );
  }

  // Use default avatar with initials
  return (
    <DefaultAvatar
      name={name}
      email={email}
      size={size}
      sx={sx}
      {...props}
    />
  );
};

export default UserAvatar;
